import { MessageMetadata } from '../ChatFramework.types';
import { loadSelectedTransactionsFromStorage } from '../../../selectedTransactions.utils';
import {
  BotConstants,
  buildConnectToAgentWithArticleConfig,
  checkAiABTestingPassed,
  checkAiBetABTestingPassed,
  checkAiTransactionABTestingPassed,
  createMessageHistory,
  EventType,
  getRandomInt,
  ItemType,
  spawnMediaMessage,
  mergeConnectConfig,
  randomId,
  spawnSystemMessage,
  spawnSystemMessageFromAI,
  spawnUserMessageForAI,
  spawnUserSystemMessage,
} from '../../lib';
import {
  AiMessageForUser,
  ArticleSystemOption,
  BaseEvent,
  BaseExternalEvent,
  BaseMessage,
  ConnectToAgent,
  GuestLoginEvent,
  InitialConnectAiEvent,
  PreSupportSelectionEvent,
  RateAiResponseEvent,
  ResolutionEvent,
  ResumeAIChatEvent,
  UserFileForAIEvent,
  StartSelectedEntityFlowEvent,
  UserInputSubmittedTransactionForAiEvent,
  UserLoginEvent,
  UserMessageForAI,
  UserMessageForAIEvent,
} from '../../types';
import { AI_EVENTS, analytics } from '@/common/logs';
import { reportingService } from '@/common/reporting';
import { parseErrorMessage } from '@/common/error';
import * as PleasedApi from '@/common/api/api.pleased';
import { switcher, Switcher } from '../switcher';
import { deepClone, replacePlaceholders, waitFor } from '@/common/utils';
import { i18n } from '@/plugins/i18n';
import * as env from '@/common/environment';
import * as ApiModel from '@/common/api/models.pleased';
import { ResolutionType } from '../../enum/ResolutionType';
import { generateUpdatedEvent } from '../ChatFramework.utils';
import {
  isArticleFromListNavigationEvent,
  isArticleOptionEvent,
  isArticleSupportRequestEvent,
  isBrowseOtherArticlesEvent,
  isIssueNotListedArticlesEvent,
  isOverlongResolutionEvent,
  isSectionOptionEvent,
  isStaleConversationResolutionEvent,
  isAiEligibleExternalEvent,
  isConnectPreSupportSelectionEvent,
  isUserLoginFromAIEvent,
} from './AiService.eventUtils';
import {
  AiServiceChatFrameworkInterface,
  GetAiResponseParams,
  HandleAiResponseActionParams,
  HandleAiResponseErrorParams,
  HandleAiResponseParams,
} from './AiService.types';
import { MemberType, MessageType } from '../../enum';
import {
  getAiEventFinishFlowConfig,
  getAiFinishFlowConfig,
  getAiGuestFallbackConfig,
  getAiGuestStartFlowConfig,
  isUserEligibleForAiFlow,
  setRecentlyConnectedAI,
} from './AiService.utils';
import { getAiActionSelectorTypes, isAiActionShowSelector } from './AiService.static';
import { WatchdogUtil } from '../WatchdogUtil';
import { getAiChatQueueMessages } from '../messages';
import { getExternalTransactionTypes, TRANSACTION_TYPE_NAME_ENUM } from '@/common/transaction-utils';
import PleasedChatClient from '@/vendor/pleased-sdk';
import { ResumeAIChatMsg } from '@/common/types';
import { isGuestLoginEvent, isResumeAIChat, isUserLoginEvent, isStartSelectedEntityFlowEvent } from '../../lib/EventType';
export class AiService {
  private chatFramework: AiServiceChatFrameworkInterface;
  private chatSdk: PleasedChatClient | null = null;
  private stopFeedbackTimer: (() => void) | null = null;

  private pendingAiAction: ApiModel.AiResponseAction | null = null;
  private pendingAiMessages: BaseMessage[] = [];

  protected aiEventSwitch!: Switcher<BaseEvent | BaseExternalEvent>;
  protected aiConnectEventSwitch!: Switcher<BaseEvent>;
  protected aiConnectExternalEventSwitch!: Switcher<BaseExternalEvent>;

  private aiMessageCounter = 0;
  private AI_MESSAGE_LIMIT = 15;
  private readonly METADATA_MESSAGE_PREFIX = '[METADATA] ';

  constructor (chatFramework: AiServiceChatFrameworkInterface) {
    this.chatFramework = chatFramework;
    this.setupEventSwitch();
  }

  // Add new events here separately (without inner checks) for each "when" case, so we can
  // check if AI Service supports it.
  protected setupEventSwitch () {
    // This switcher relates to events AI Service need to handle – AI Related events
    this.aiEventSwitch = switcher<BaseEvent | BaseExternalEvent>()
      .when(EventType.isRateAiResponseEvent, this.handleRateAiResponseEvent)
      .when(EventType.isUserMessageForAIEvent, this.handleMessageFromUserForAI)
      .when(EventType.isUserFileForAIEvent, this.handleFileFromUserForAI)
      .when(EventType.isUserInputSubmittedTransactionForAiEvent, this.handleUserTransactionMetadata)
      .when(isOverlongResolutionEvent, this.handleOverlongAiConversationResolution)
      .when(isStaleConversationResolutionEvent, this.handleStaleAiConversation)
      .when(isUserLoginFromAIEvent, this.handleUserLoginFromAI)
      .none(e => console.error('[AiS] Unhandled event passed', { e }));

    // This switcher relates to BaseEvent (default flow) AI Insertion points/Connect to AI
    this.aiConnectEventSwitch = switcher<BaseEvent>()
      .when(isSectionOptionEvent, this.onSectionSelectOption)
      .when(isArticleOptionEvent, this.onArticleSelectOption)
      .when(isArticleFromListNavigationEvent, this.onArticleSelectOption)
      .when(this.isBrowseOtherArticlesEventSupported, this.onArticleSelectOption)
      .when(isIssueNotListedArticlesEvent, this.onIssueNotListedArticles)
      .when(isConnectPreSupportSelectionEvent, this.onConnectPreSupportSelection)
      .none(e => console.error('[AiS] Unhandled connect event passed', { e }));

    // This switcher relates to BaseExternalEvent AI Insertion points/Connect to AI
    this.aiConnectExternalEventSwitch = switcher<BaseExternalEvent>()
      .when(isArticleSupportRequestEvent, this.connectToAI)
      .when(isResumeAIChat, this.handleResumeAIChat)
      .when(isUserLoginEvent, this.handleLoginEvent)
      .when(isGuestLoginEvent, this.handleGuestLoginEvent)
      .when(isStartSelectedEntityFlowEvent, this.handleStartSelectedEntityFlow)
      .none(e => console.error('[AiS] Unhandled connect external event passed', { e }));
  }

  public testAiEventHandler (event: BaseEvent | BaseExternalEvent) {
    return this.aiEventSwitch.test(event);
  }

  public submitAiEvent (event: BaseEvent | BaseExternalEvent) {
    this.aiEventSwitch.match(event);
  }

  public testAiConnectEventHandler (event: BaseEvent) {
    if (!this.checkEventCanConnectToAI(event)) return false;
    return this.aiConnectEventSwitch.test(event);
  }

  public submitAiConnectEvent (event: BaseEvent) {
    this.aiConnectEventSwitch.match(event);
  }

  public testAiConnectExternalEventHandler (event: BaseExternalEvent) {
    if (!isAiEligibleExternalEvent(event)) return false;
    if (!this.checkEventCanConnectToAI(event)) return false;
    return this.aiConnectExternalEventSwitch.test(event);
  }

  public submitAiConnectExternalEvent (event: BaseExternalEvent) {
    this.aiConnectExternalEventSwitch.match(event);
  }

  private checkEventCanConnectToAI (event: BaseExternalEvent | BaseEvent) {
    const isExternalEvent = EventType.isExternalEvent(event);

    // AI TODO: separate properties getter based on event if we'll need more fields,
    // since currently we take properties related only for articles.
    // We can refactor it in a "switcher" way for preparing those properties based on event type and
    // use it before calling checkCanConnectToAI in tester functions
    const connectConfig = (isExternalEvent
      ? event.payload.supportOptions
      : (event as BaseEvent).payload?.selected) as ConnectToAgent;

    return this.checkCanConnectToAI(connectConfig);
  }

  public checkCanConnectToAI (connectConfig: ConnectToAgent) {
    const options = this.chatFramework.getOptions();

    const {
      userId,
      isUserLoggedIn,
      userLoyaltyTier,
    } = options.user;

    const {
      openAiDown,
      aiEnabledPerBrand,
      aiEnabledPerSession,
      aiAbTestingEnabled,
      aiAbTestPercentage,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,
    } = options.ai;

    const canConnectToAI = isUserEligibleForAiFlow({
      userId,
      userLoyaltyTier,
      isUserLoggedIn,
      openAiDown,
      aiEnabledPerBrand,
      aiEnabledPerSession,
      aiAbTestingEnabled,
      aiEndUserComeBackTime,
      aiTransactionEnabled,
      aiTransactionAbRatio,
      aiGuestUsersEnabled,
      aiGuestUsersAbRatio,
      abTestPercentage: aiAbTestPercentage,
      skipAiChat: connectConfig?.skipAiChat,
      enableMetadataRules: connectConfig?.enableMetadataRules,
    });

    return canConnectToAI;
  }

  protected connectToAI = (event: InitialConnectAiEvent, tags?: MessageMetadata[], aiMessage?: string) => {
    const { user, guest, ai } = this.chatFramework.getOptions();

    const isExternalEvent = EventType.isExternalEvent(event);

    const connectConfig = (isExternalEvent
      ? event.payload.supportOptions
      : (event as BaseEvent).message.config?.connect) as ConnectToAgent;

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_RUN_CONNECT_TO_AI,
      metadata: {
        connectConfig,
        isUserLoggedIn: user.isUserLoggedIn,
        isGuestFilledDetails: guest.filledDetails,
      },
    });

    // If user is not logged in or created guest account – we require to do so
    if (!user.isUserLoggedIn && !guest.filledDetails) {
      const config = getAiGuestStartFlowConfig(connectConfig);

      // Handle Login message so we have correct "Initial Event"
      this.chatFramework.addMessage(
        spawnSystemMessageFromAI({
          message: config.loginRequired ? i18n.t('chat.sys.login.required') as string : i18n.t('chat.sys.login.optional') as string,
          sessionId: analytics.sessionId || '',
          initialEvent: generateUpdatedEvent(event, config),
        })
      );

      this.chatFramework.connectToAgentFromConfig(config);
      return;
    }

    const category = connectConfig?.category || '';
    const article = connectConfig?.articleId || ''; // Check if title is here

    const appName = env.get('appName') || '';
    const fallbackMessage = i18n.t('chat.msg.ai.welcome_default', {
      appName,
      categoryName: category,
      requesterName: user.userName,
    }) as string;

    const messageText = replacePlaceholders(aiMessage || ai.aiWelcomeMessage || fallbackMessage, {
      category: { name: category },
      requester: { name: user.userName },
      app: { name: appName },
    });

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_SESSION_START,
      metadata: { message: messageText, category, article },
    });

    const initialMessage = spawnSystemMessageFromAI({
      message: messageText,
      sessionId: analytics.sessionId || '',
      initialEvent: event,
    });

    // Promise for interception check
    this.chatFramework.addMessage(Promise.resolve(initialMessage), { tags, event });
    this.chatFramework.emitter.emit('preConnectToAI');

    return true;
  }

  public async getAiResponse (params: GetAiResponseParams) {
    const { message, isFirstRequest, welcomeMessage, metadata, resumeAiSessionId } = params;
    const { ai, user, guest } = this.chatFramework.getOptions();

    const aiAbTestPassed = checkAiABTestingPassed({
      userId: user.userId,
      enableAbTesting: ai.aiAbTestingEnabled,
      abValue: ai.aiAbTestPercentage,
    });

    const aiTransactionAbTestPassed = checkAiTransactionABTestingPassed({
      userId: user.userId,
      enableAbTesting: ai.aiTransactionEnabled,
      abValue: ai.aiTransactionAbRatio,
      aiAbTestPassed,
    });

    const aiBetAbTestPassed = checkAiBetABTestingPassed({
      userId: user.userId,
      enableAbTesting: ai.aiBetEnabled,
      abValue: ai.aiBetAbRatio,
      aiAbTestPassed,
    });

    const requestParams = {
      user_id: user.isUserLoggedIn ? `${user.userId}` : `${guest.userId}`,
      user_input: message,
      session_id: resumeAiSessionId || analytics.sessionId || '',
      first_request: isFirstRequest,
      transaction_enable: aiTransactionAbTestPassed,
      bet_enable: aiBetAbTestPassed,
      ...(isFirstRequest
        ? {
          userLogs: reportingService.getLogs(),
          userSelections: getAiChatQueueMessages(this.chatFramework.getMessages()),
          welcomeMessage,
        }
        : {}),
      metadata,
    };

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_USER_REQUEST,
      metadata: { ...requestParams },
    });

    if (user.isUserLoggedIn) {
      return PleasedApi.getAiChatResponse(requestParams);
    }

    return PleasedApi.getAiChatResponseGuest(requestParams);
  }

  private handleAiResponseAction (params: HandleAiResponseActionParams) {
    const { event, action, metadata } = params;

    if (!action) {
      return;
    }

    // TODO: if we'll have too many lines here, then separate handlers
    const actionSwitcher = switcher<ApiModel.AiResponseAction>()
      .when(ApiModel.AiResponseAction.CONNECT_TO_AGENT, () => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_CONNECT_SUPPORT_RESPONSE,
          metadata,
        });

        const finishFlowConnectConfig = getAiEventFinishFlowConfig(event);
        this.connectToAgentFromAiFlow(finishFlowConnectConfig);
      })
      .when(ApiModel.AiResponseAction.END_CHAT, () => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_END_CHAT_RESPONSE,
          metadata,
        });

        this.finishAiFlowPerSession();
        this.chatFramework.emitter.emit('endAiConversation');
      })
      .when(ApiModel.AiResponseAction.TICKET_END_CHAT, () => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_TICKET_END_CHAT_RESPONSE,
          metadata,
        });

        this.finishAiFlowPerSession();
        this.chatFramework.emitter.emit('endAiConversation');
      })
      .when(isAiActionShowSelector, action => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_SHOW_TRANSACTION_SELECTOR,
          metadata,
        });

        const transactionTypes = getAiActionSelectorTypes(action);

        if (!this.chatFramework.getOptions().user.isUserLoggedIn) {
          this.chatFramework.emitter.emit('setSelectorTypes', transactionTypes);
          return;
        }

        this.chatFramework.emitter.emit('showTransactionSelector', transactionTypes);
      })
      .when(ApiModel.AiResponseAction.COLLECT_FILE, () => {
        analytics.trackAiEvent({
          name: AI_EVENTS.AI_COLLECT_FILE,
          metadata,
        });

        this.chatFramework.emitter.emit('enableUserFileUploadForAi', true);
      })
      .none(() => {
        console.error('Unhandled action', action);
      });

    return actionSwitcher.match(action);
  }

  private handleRateAiResponseEvent = (event: RateAiResponseEvent) => {
    const {
      message,
      payload: { selected },
    } = event;

    this.chatFramework.emitter?.emit('rateAiResponse', {
      message,
      rate: selected,
    });
  };

  private handleOverlongAiConversationResolution = (event: ResolutionEvent) => {
    const { message } = event;
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      analytics.trackAiEvent({
        name: AI_EVENTS.AI_OVERLONG_CONVERSATION_CONNECT_SUPPORT,
        metadata: { message: message.message },
      });

      // Handle guest case
      const supportOptions = mergeConnectConfig(message.config?.connect as ConnectToAgent, {
        loginRequired: true,
      });

      this.chatFramework.connectArticleWithAcknowledgement({
        supportOptions,
        event,
        history: createMessageHistory(event, message),
      });
    } else if (selected === ResolutionType.NO) {
      this.chatFramework.removeMessage(message);
    }
  };

  private onSectionSelectOption = (event: BaseEvent) => {
    const selected = event.payload.selected;

    this.chatFramework.emitter?.emit('selectOption', {
      selected,
      message: event.message,
      event,
    });

    const updatedEvent = generateUpdatedEvent(event, selected);
    this.addUserSelectionMessage(updatedEvent);

    this.connectToAI(updatedEvent, ['from:system', 'fetching', 'opt.section']);
  };

  private onArticleSelectOption = (event: BaseEvent) => {
    const selected = event.payload.selected as ArticleSystemOption;

    this.chatFramework.emitter?.emit('selectOption', {
      selected,
      message: event.message,
      event,
    });

    // For article only case – isArticleOption, move to separate function
    const parentWithArticleConfig = buildConnectToAgentWithArticleConfig(event.message?.config?.connect!, selected);
    const updatedEvent = generateUpdatedEvent(event, parentWithArticleConfig);
    this.addUserSelectionMessage(updatedEvent);

    this.connectToAI(updatedEvent, ['from:system', 'fetching', 'opt.article']);
  };

  private isBrowseOtherArticlesEventSupported (event: BaseEvent) {
    const isCorrectEvent = isBrowseOtherArticlesEvent(event);
    if (!isCorrectEvent) return false;

    // Extra check for this option to be show per certain ENVs
    const { showInBrowseOtherArticles } = env.get('AI') || {
      showInBrowseOtherArticles: false,
    };

    return Boolean(showInBrowseOtherArticles);
  }

  private onIssueNotListedArticles = (event: BaseEvent) => {
    const selected = event.payload.selected;

    this.chatFramework.emitter?.emit('selectOption', {
      selected,
      message: event.message,
      event,
    });

    const hardcodedConnectConfigParams = { showDescriptionBox: true };
    const updatedEvent = generateUpdatedEvent(event, hardcodedConnectConfigParams);
    this.chatFramework.addMessage(
      spawnUserSystemMessage({
        sendToChat: true,
        message: i18n.t('chat.sys.msg.issue_not_listed') as string,
        ...(updatedEvent ? { history: createMessageHistory(updatedEvent, updatedEvent.message) } : {})
        ,
      })
    );

    this.connectToAI(updatedEvent);
  };

  private onConnectPreSupportSelection = (event: PreSupportSelectionEvent) => {
    const { selected } = event.payload;
    this.chatFramework?.emitter.emit('selectOption', { selected, message: event.message, event });
    this.connectToAI(event);
  };

  private addUserSelectionMessage (event: BaseEvent, helpTopic?: string) {
    const selected = event?.payload?.selected || '';
    const helpTopicText = helpTopic || selected.title;

    this.chatFramework.addMessage(
      spawnUserSystemMessage({
        sendToChat: true,
        message: i18n.t('chat.user.response.message.text', { name: helpTopicText }) as string,
        interactiveMessageParts: [i18n.t('chat.user.response.message.part', { name: helpTopicText }) as string, helpTopicText],
        ...(event ? { history: createMessageHistory(event, event.message) } : {}),
      })
    );
  }

  // TODO: Refactor this logic part
  // Methods used to identify if user is connected to AI + get initial config
  // Currently based on message list, ideally I'd update with more clear/simple solution
  private getInitialAiMessage (): AiMessageForUser | undefined {
    return this.chatFramework
      .getMessages()
      .find(m => ItemType.isAiMessageForUser(m) && m.initialEvent) as AiMessageForUser;
  }

  private checkIsFirstRequestToAI (): boolean {
    const messagesFromUser = this.chatFramework.getMessages().filter(m => ItemType.isUserMessageForAI(m));
    return messagesFromUser.length === 0;
  }

  private checkIsGuestTxAction (action: ApiModel.AiResponseAction): boolean {
    const { user } = this.chatFramework.getOptions();
    return !user.isUserLoggedIn && isAiActionShowSelector(action);
  }

  private prepareConnectedAiEvent (
    initialEvent: InitialConnectAiEvent,
    hardcodedParams: Partial<ConnectToAgent> = { aiConversation: true },
  ): {
    connectedAiEvent: InitialConnectAiEvent;
    connectConfig: ConnectToAgent;
  } {
    const isExternalEvent = EventType.isExternalEvent(initialEvent);
    const connectedAiEvent = deepClone(initialEvent);

    if (isExternalEvent) {
      connectedAiEvent.payload.supportOptions = {
        ...connectedAiEvent.payload.supportOptions,
        ...hardcodedParams,
      };
    } else if (EventType.isSystemEvent(connectedAiEvent)) {
      connectedAiEvent.message.config = {
        ...connectedAiEvent.message.config,
        connect: {
          ...connectedAiEvent.message.config?.connect!,
          ...hardcodedParams,
        },
      };
    }

    const connectConfig = (isExternalEvent
      ? connectedAiEvent.payload.supportOptions
      : (connectedAiEvent as BaseEvent).message.config?.connect) as ConnectToAgent;

    return { connectedAiEvent, connectConfig };
  }

  private getPreparedInitialEvent (hardcodedParams?: Partial<ConnectToAgent>) {
    const initialMessage = this.getInitialAiMessage();
    const initialEvent = initialMessage?.initialEvent;

    if (!initialMessage || !initialEvent) {
      console.error('[CF] No initialEvent found for AI message');
      return;
    }

    return this.prepareConnectedAiEvent(initialEvent, hardcodedParams);
  }

  // End of methods list to work with initial event/config or identify if connected to AI

  private createUserMessageForAI (message: string, initialEvent: InitialConnectAiEvent): UserMessageForAI {
    const userMessage = spawnUserMessageForAI({
      message,
      initialEvent,
      sessionId: analytics.sessionId || '',
    }) as UserMessageForAI;

    this.aiMessageCounter += 1;
    this.chatFramework.addMessage(userMessage);

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_USER_INPUT,
      selection: userMessage.id,
      metadata: { message: userMessage.message, messageId: userMessage.id },
    });

    return userMessage;
  }

  private async handleAiChatCreation (params: {
    firebaseToken: string,
    connectConfig: ConnectToAgent;
  }) {
    const { firebaseToken, connectConfig } = params;
    const { user } = this.chatFramework.getOptions();
    const customerId = user.userId ? Number(user.userId) : null;

    // We want to subscribe for FB events if user is logged in
    if (user.isUserLoggedIn && user.userToken && customerId) {
      this.chatSdk = new PleasedChatClient();
      this.chatSdk.init({
        access_token: user.userToken,
        pleased_url: env.get('url'),
        brand_subdomain: env.get('brandCode'),
      });

      this.chatSdk.setupFirebaseAiChat({
        aiSessionId: connectConfig?.resumeAiSessionId || analytics.sessionId || '',
        customerId,
        firebaseToken,
        onIntervene: this.handleInterveneAiChat,
      });
    }

    this.chatFramework.emitter.emit('connectedToAI', { connectConfig });
    setRecentlyConnectedAI(user.userId);
  }

  private async handleFirstResponse (
    aiResponse: ApiModel.AiChatResponsePayload, // adjust type as needed
    isFirstRequest: boolean,
    connectConfig: ConnectToAgent
  ): Promise<BaseMessage | null> {
    if (!aiResponse) return null;

    const aiResponseMessage = spawnSystemMessageFromAI({
      message: aiResponse.response[0],
      sessionId: analytics.sessionId || '',
      aiResponse,
    });

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_RESPONSE_FIRST,
      selection: aiResponseMessage.id,
      metadata: {
        message: aiResponseMessage.message,
        messageId: aiResponseMessage.id,
        aiResponse,
      },
    });

    // TODO: make handleFirstResponse + handleFollowUpResponses
    // as one function like handleAiResponseMessages since they will be identical after moving this logic
    if (isFirstRequest) {
      this.handleAiChatCreation({ firebaseToken: aiResponse.firebaseToken, connectConfig });
    }

    const options = this.chatFramework.getOptions();
    const { aiGuestUsersMessage } = options.ai;

    // We have to handle it here to overwrite default message handling behavior
    if (this.checkIsGuestTxAction(aiResponse.action)) {
      this.pendingAiAction = aiResponse.action;
      this.pendingAiMessages.push(aiResponseMessage);
      this.chatFramework.emitter.emit('disableUserInputForAi', true);

      const loginMessage = spawnSystemMessage({
        type: MessageType.USER_LOGIN_FROM_AI,
        message: aiGuestUsersMessage,
      });

      return loginMessage;
    }

    return aiResponseMessage;
  }

  private async handleFollowUpResponses (
    aiResponse: ApiModel.AiChatResponsePayload
  ): Promise<ApiModel.AiChatResponsePayload> {
    if (!aiResponse || aiResponse.response.length <= 1) return aiResponse;

    for (let i = 1; i < aiResponse.response.length; i++) {
      const delayBeforeMessage = getRandomInt(1, 4) * 1000;
      const delayPromise = waitFor(delayBeforeMessage);

      const aiResponseMessage = spawnSystemMessageFromAI({
        message: aiResponse.response[i],
        sessionId: analytics.sessionId || '',
        aiResponse,
      });

      if (this.checkIsGuestTxAction(aiResponse.action)) {
        this.pendingAiMessages.push(aiResponseMessage);
        continue;
      }

      await this.chatFramework.addMessage(
        delayPromise.then(() => {


          analytics.trackAiEvent({
            name: AI_EVENTS.AI_RESPONSE_FOLLOW_UP,
            selection: aiResponseMessage.id,
            metadata: {
              messageIndex: i,
              message: aiResponseMessage.message,
              messageId: aiResponseMessage.id,
              aiResponse,
            },
          });

          return aiResponseMessage;
        })
      );
    }

    return aiResponse;
  }

  private handleOverlongConversation (aiResponse: ApiModel.AiChatResponsePayload, connectConfig: ConnectToAgent): void {
    if (!aiResponse) return;
    // Skip overlong conversation if AI response has action
    if (aiResponse.action) return;

    this.aiMessageCounter += aiResponse.response.length;
    if (this.aiMessageCounter < this.AI_MESSAGE_LIMIT) return;

    const resolutionConnectConfig = getAiFinishFlowConfig(connectConfig);
    this.aiMessageCounter = 0;
    this.chatFramework.addMessage(
      spawnSystemMessage({
        type: MessageType.OVERLONG_AI_CONVERSATION_RESOLUTION,
        message: i18n.t('chat.sys.msg.ai.overlong_conversation') as string,
        config: { connect: { ...resolutionConnectConfig, overlongConversation: true } },
      })
    );
  }

  private handleMessageFromUserForAI = async (event: UserMessageForAIEvent) => {
    const { message } = event.payload;

    this.stopFeedbackTimer?.();
    this.chatFramework.emitter.emit('disableUserInputForAi', true);

    // Determine initialMessage with event/connectConfig
    const initialMessage = this.getInitialAiMessage();
    const initialEvent = initialMessage?.initialEvent;

    if (!initialMessage || !initialEvent) {
      console.error('[CF] No initialEvent found for AI message');
      return;
    }

    try {
      // Add aiConversation flag here to connect config
      const { connectedAiEvent, connectConfig } = this.prepareConnectedAiEvent(initialEvent);

      // We need to check if it's first request to AI before adding user message to AI
      const isFirstRequest = this.checkIsFirstRequestToAI();

      // We save connectedAiEvent in message here, since in case of error this message will be removed
      this.createUserMessageForAI(message, connectedAiEvent);

      // We use initial event here, without any modifications if case of error
      const aiResponsePromise = this.getAiResponse({
        message,
        isFirstRequest,
        welcomeMessage: initialMessage.message,
        resumeAiSessionId: connectConfig?.resumeAiSessionId,
      });

      await this.handleAiResponse({ event: initialEvent, userMessage: message, isFirstRequest, aiResponsePromise });
    } finally {
      this.chatFramework.emitter.emit('disableUserInputForAi', false);
    }
  };

  private userEventsMsg = (events: any) => {
    let msg = 'Events: ';
    events.forEach((event: any, index: number) => { msg += `${event.home} vs ${event.away} (${event.eventId})${index === events.length - 1 ? '' : ', '}`; });
    return msg;
  }

  private handleUserTransactionMetadata = async (event: UserInputSubmittedTransactionForAiEvent) => {
    console.log('handleUserTransactionMetadata', event);
    const { transactionType, transactionMetadata } = event.payload;

    const transactionTypeName = TRANSACTION_TYPE_NAME_ENUM[transactionType];
    const isTransactionSelected = !transactionMetadata.transactionNotSelected;
    // This is required to pass type issue due to low version of typescript
    const selectedTransaction = transactionMetadata as ApiModel.ExtendedSelectedTransStatement;

    const message = (isTransactionSelected
      ? i18n.t('chat.sys.msg.ai.transaction_help', {
        transactionType: transactionTypeName,
        transactionId: selectedTransaction.tradeId,
        userEvents: selectedTransaction?.__USER_EVENTS?.length > 0 ? this.userEventsMsg(selectedTransaction.__USER_EVENTS) : '',
      })
      : i18n.t('chat.sys.msg.ai.transaction_not_listed_help')) as string;

    // Determine initialMessage with event/connectConfig
    const initialMessage = this.getInitialAiMessage();
    const initialEvent = initialMessage?.initialEvent;
    const isFirstRequest = this.checkIsFirstRequestToAI();

    this.chatFramework.addMessage(
      spawnUserMessageForAI({
        message,
      })
    );

    this.chatFramework.emitter.emit('disableUserInputForAi', true);

    if (!initialMessage || !initialEvent) {
      console.error('[CF] No initialEvent found for AI message');
      return;
    }

    try {
      const { connectConfig } = this.prepareConnectedAiEvent(initialEvent);
      // We use initial event here, without any modifications if case of error
      const aiResponsePromise = this.getAiResponse({
        message: `${this.METADATA_MESSAGE_PREFIX}${message}`,
        isFirstRequest,
        metadata: transactionMetadata,
        resumeAiSessionId: connectConfig?.resumeAiSessionId,
      });

      await this.handleAiResponse({ event: initialEvent, userMessage: null, aiResponsePromise, isFirstRequest });
    } finally {
      this.chatFramework.emitter.emit('disableUserInputForAi', false);
    }
  };

  private handleFileFromUserForAI = async (event: UserFileForAIEvent) => {
    const { fileUrl, fileName, contentType, attachmentId } = event.payload;

    const message = i18n.t('chat.sys.msg.ai.file_help') as string;
    const isFirstRequest = this.checkIsFirstRequestToAI();

    this.chatFramework.addMessage([
      spawnUserMessageForAI({
        message,
        sendToChat: true,
      }),
      spawnMediaMessage({
        message,
        sendToChat: true,
        attachment: {
          id: attachmentId,
          url: fileUrl,
          mimeType: contentType,
          name: fileName,
        },
        memberType: MemberType.VISITOR
      })
    ]);


    this.chatFramework.emitter.emit('disableUserInputForAi', true);

    // Determine initialMessage with event/connectConfig
    const initialMessage = this.getInitialAiMessage();
    const initialEvent = initialMessage?.initialEvent;

    if (!initialMessage || !initialEvent) {
      console.error('[CF] No initialEvent found for AI message');
      return;
    }

    try {
      const { connectConfig } = this.prepareConnectedAiEvent(initialEvent);
      // We use initial event here, without any modifications if case of error
      const aiResponsePromise = this.getAiResponse({
        message: `${this.METADATA_MESSAGE_PREFIX}${message}`,
        isFirstRequest,
        metadata: { fileUrl, attachmentId },
        resumeAiSessionId: connectConfig?.resumeAiSessionId,
      });

      await this.handleAiResponse({ event: initialEvent, userMessage: null, isFirstRequest, aiResponsePromise });
    } finally {
      this.chatFramework.emitter.emit('disableUserInputForAi', false);
    }
  };

  // We need to handle Promise here for "Typing" message
  private handleAiResponse = async (params: HandleAiResponseParams) => {
    const { event, userMessage, aiResponsePromise, isFirstRequest } = params;

    // Clear a previous feedback timer if any, setup a new one
    this.stopFeedbackTimer?.();
    this.startTimedFeedback();

    // Add aiConversation flag here to connect config
    const { connectConfig, connectedAiEvent } = this.prepareConnectedAiEvent(event);

    try {
      // We're adding "Typing" message for first result here.
      this.chatFramework.addMessage(
        aiResponsePromise.then(aiResponse => this.handleFirstResponse(aiResponse, isFirstRequest, connectConfig))
      );

      const aiResponse = await aiResponsePromise;

      // If there're multiple responses, we're adding them with delay between messages
      this.handleFollowUpResponses(aiResponse)
        // Handle AI Action if any provided
        .then(() => this.handleAiResponseAction({
          event: connectedAiEvent,
          action: aiResponse.action,
          metadata: { userMessage, aiResponse },
        }))
        // Once all AI Messages added, we check for overlong conversation, if no action passed.
        .then(() => this.handleOverlongConversation(aiResponse, connectConfig));
    } catch (error) {
      console.error('Error in AI flow', error);

      const eventConnectConfig = getAiEventFinishFlowConfig(event);
      const errorConnectConfig = isFirstRequest ? eventConnectConfig : connectConfig;

      this.handleAiResponseError({
        connectConfig: errorConnectConfig,
        userMessage,
        error: parseErrorMessage(error),
        isFirstRequest,
      });
    }
  };

  private handleAiResponseError = (params: HandleAiResponseErrorParams) => {
    const { connectConfig, userMessage, error, isFirstRequest } = params;

    console.error('Error in AI flow', error);
    analytics.trackAiEvent({
      name: AI_EVENTS.AI_ERROR_RESPONSE,
      metadata: { message: userMessage, error },
    });

    // Otherwise show error message and connect to agent
    this.connectToAgentFromAiFlow({ ...connectConfig, isFirstRequest });

    this.chatFramework.addMessage(
      spawnSystemMessage({
        type: MessageType.CHAT_MESSAGE,
        message: i18n.t('chat.sys.msg.ai.response_error') as string,
        sendToChat: true,
      })
    );
  };

  private finishAiFlowPerSession () {
    this.chatFramework.setAiOptions({ aiEnabledPerSession: false });
    this.stopFeedbackTimer?.();
  }

  private connectToAgentFromAiFlow (connectConfig: ConnectToAgent) {
    this.finishAiFlowPerSession();
    this.chatFramework.connectToAgentFromConfig(connectConfig);
  }

  // method to start timed ai feedback
  private startTimedFeedback () {
    const options = this.chatFramework.getOptions();
    const { enableTimedAiFeedback, timedAiFeedbackNoMessagesTimeInSeconds = 0 } = options.ai;

    // This should not be a case, but we'll keep it here for safety
    if (this.stopFeedbackTimer) {
      return;
    }

    if (!enableTimedAiFeedback || !timedAiFeedbackNoMessagesTimeInSeconds) {
      return;
    }

    const stopTimer = WatchdogUtil.startWatchdog(
      timedAiFeedbackNoMessagesTimeInSeconds * 1000,
      5000,
      this.handleTimedFeedback
    );

    this.stopFeedbackTimer = () => {
      stopTimer();
      this.stopFeedbackTimer = null;
    };
  }

  private handleTimedFeedback = () => {
    const options = this.chatFramework.getOptions();
    const { timedAiFeedbackConfirmationMessageText } = options.ai;

    analytics.trackAiEvent({
      name: AI_EVENTS.AI_STALE_CONVERSATION,
      metadata: { text: timedAiFeedbackConfirmationMessageText },
    });

    this.stopFeedbackTimer?.();
    this.chatFramework.emitter.emit('disableUserInputForAi', true);

    this.chatFramework.addMessage(
      spawnSystemMessage({
        type: MessageType.STALE_AI_CONVERSATION_RESOLUTION,
        message: timedAiFeedbackConfirmationMessageText,
      })
    );
  };

  private handleStaleAiConversation = (event: ResolutionEvent) => {
    // Allow user to type and show resume conversation message
    if (event.payload.selected === ResolutionType.YES) {
      const options = this.chatFramework.getOptions();
      const { timedAiFeedbackResumeMessageText } = options.ai;

      analytics.trackAiEvent({
        name: AI_EVENTS.AI_STALE_CONVERSATION_RESUME,
        metadata: { text: timedAiFeedbackResumeMessageText },
      });

      this.chatFramework.emitter.emit('disableUserInputForAi', false);
      this.chatFramework.addMessage(
        spawnSystemMessage({
          type: MessageType.CHAT_MESSAGE,
          message: timedAiFeedbackResumeMessageText,
        })
      );
      // Otherwise show csat
    } else if (event.payload.selected === ResolutionType.NO) {
      analytics.trackAiEvent({
        name: AI_EVENTS.AI_STALE_CONVERSATION_END,
      });

      this.finishAiFlowPerSession();
      this.chatFramework.emitter.emit('endAiConversation');
      this.chatFramework.emitter.emit('endChat');
    }
  };

  private handleInterveneAiChat = (chatId: number) => {
    analytics.track({
      name: AI_EVENTS.AI_INTERVENE_CHAT,
      metadata: { chatId },
    });

    this.chatFramework.emitter.emit('interveneAiChat', chatId);
    this.chatSdk?.disablePleasedTypingListener();
  };

  private handleResumeAIChat = (event: ResumeAIChatEvent) => {
    const payload = event.payload;
    const messages = payload.messages;
    const resumeAiSessionId = payload.sessionId;
    const lastMessage = messages[messages.length - 1];

    // Load selectedTransactionsData from localStorage for resume AI functionality
    const storedSelectedTransactionsData = loadSelectedTransactionsFromStorage();

    const config = {
      connect: true,
      liveChat: true,
      loginRequired: true,
      openRequest: true,
      skipAiChat: false,
      transactionMessage: '',
      voiceChat: true,
      resumeAiSessionId,
      aiConversation: true,
      // Only add selectedTransactionsData if it exists in localStorage
      ...(storedSelectedTransactionsData && { selectedTransactionsData: storedSelectedTransactionsData }),
    };
    analytics.trackAiEvent({
      name: AI_EVENTS.AI_RESUME_CHAT,
      selection: resumeAiSessionId,
    });

    const initialEvent = {
      message: { config: { connect: config } },
    } as InitialConnectAiEvent;

    this.chatFramework.addMessage(
      messages.map((msg: ResumeAIChatMsg) => {
        const type = msg.isAiMessage ? MemberType.AGENT : MemberType.VISITOR;
        if (type === MemberType.AGENT) {
          return spawnSystemMessageFromAI({
            message: msg.content,
            sessionId: msg.aiSessionId,
            timestamp: (msg.createdAt?.valueOf() || Date.now() + 1) as number,
            initialEvent: initialEvent as any,
          });
        }
        return spawnUserMessageForAI({
          message: msg.content,
          sessionId: msg.aiSessionId,
          timestamp: (msg.createdAt?.valueOf() || Date.now() + 1) as number,
        });
      })
    );

    this.handleAiChatCreation({ connectConfig: config, firebaseToken: payload.firebaseToken });
    this.handleAiResponseAction({
      event: initialEvent as InitialConnectAiEvent,
      action: lastMessage.action,
      metadata: { message: lastMessage.content, aiResponse: lastMessage },
    });
  }

  private handleGuestLoginEvent = (event: GuestLoginEvent) => {
    analytics.trackAiEvent({
      name: AI_EVENTS.AI_GUEST_LOGIN,
    });

    this.connectToAI(event);
  }

  private handleLoginEvent = (event: UserLoginEvent) => {
    // We can consider moving to boolean flag if user is connected, since we don't use initial event in code anymore
    const initialMessage = this.getInitialAiMessage();

    // If we already connected to AI, we don't need to connect again
    if (initialMessage && initialMessage.initialEvent) {
      // If we have pending messages, we need to add them to the chat
      if (this.pendingAiMessages.length > 0) {
        this.pendingAiMessages.forEach(message => this.chatFramework.addMessage(message));
        this.pendingAiMessages = [];
      }

      // If we login during AI Conversation, we might had action to process as guest
      if (this.pendingAiAction) {
        const { connectedAiEvent } = this.prepareConnectedAiEvent(initialMessage.initialEvent);
        this.handleAiResponseAction({ event: connectedAiEvent, action: this.pendingAiAction });
        this.pendingAiAction = null;
      }

      return;
    }

    this.connectToAI(event);
  }

  private handleUserLoginFromAI = (event: ResolutionEvent) => {
    const { message } = event;
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      analytics.trackAiEvent({
        name: AI_EVENTS.AI_OVERLONG_CONVERSATION_CONNECT_SUPPORT,
        metadata: { message: message.message },
      });

      // We want to make sure guest user will see login screen
      const preparedConfig = this.getPreparedInitialEvent({ loginRequired: true, aiConversation: true });
      this.chatFramework.connectToAgentFromConfig(preparedConfig?.connectConfig);
    } else if (selected === ResolutionType.NO) {
      const options = this.chatFramework.getOptions();
      const { aiGuestUsersResponseText } = options.ai;

      this.chatFramework.removeMessage(message);
      this.pendingAiAction = null;

      aiGuestUsersResponseText && this.chatFramework.addMessage(
        spawnSystemMessageFromAI({
          message: aiGuestUsersResponseText,
        })
      );

      this.chatFramework.setAiOptions({ aiEnabledPerSession: false });
      this.chatFramework.connectToAgentFromConfig(getAiGuestFallbackConfig());
    }

    this.chatFramework.emitter.emit('disableUserInputForAi', false);
  }

  private handleStartSelectedEntityFlow = (event: StartSelectedEntityFlowEvent) => {
    const { selectedEntity } = event.payload;

    this.connectToAI(event, undefined, i18n.t('chat.sys.msg.ai.selected_entity_flow') as string);
    const transactionTypes = getExternalTransactionTypes(selectedEntity.type);
    this.chatFramework.emitter.emit('showTransactionSelector', {
      type: selectedEntity.type,
      status: transactionTypes,
    });
  }
}
